import { Link, useToast } from "@chakra-ui/react";
import React from "react";

import useApi from "../../../shared/hooks/useApi";
import { VisitInstanceDailyNoteAttestationId } from "../../../shared/schema/schema";
import { formatErrorResponse } from "../../../shared/utils/format-response-error";

/** How long to wait between asking again while the server is still preparing the file. */
const POLL_INTERVAL_MS = 2500;

/**
 * Enough for a very large attestation of notes that were never rendered, and no more: past
 * this the server is stuck and the reviewer should be told, not left watching a spinner.
 */
const MAX_ATTEMPTS = 120;

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

/**
 * Downloading a whole attestation as one PDF: every note it covers, oldest visit first.
 *
 * The server builds the file from the notes' own documents, rendering any that do not exist yet
 * a few at a time. While some remain it answers "preparing" and this asks again, so a large
 * attestation shows a spinner with a count instead of one very long request that could time out.
 * Once built the file is kept, so the second download is instant.
 *
 * Kept apart from `useDailyNoteDocumentDownload`, which fetches a single note, so a call site
 * says which of the two it means.
 */
export function useAttestationPdfDownload() {
  const { api } = useApi();
  const toast = useToast();

  const [running, setRunning] = React.useState<{
    id: VisitInstanceDailyNoteAttestationId;
    remaining: number | null;
  } | null>(null);

  const downloadAll = async (attestationId: VisitInstanceDailyNoteAttestationId) => {
    // One at a time: a second click while the first is still preparing would only ask the
    // server to do the same work twice.
    if (running !== null) {
      return;
    }

    setRunning({ id: attestationId, remaining: null });

    try {
      for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
        const response = await api.get(
          "./visit_daily_note_attestations/:visitInstanceDailyNoteAttestationId/document",
          { path: { visitInstanceDailyNoteAttestationId: attestationId } }
        );

        if (response.state === "READY" && response.signedUrl !== null) {
          const signedUrl = response.signedUrl;

          // A new tab rather than navigating: the reviewer is on a table they will go back to.
          const opened = window.open(signedUrl, "_blank", "noopener,noreferrer");

          // A tab opened after a wait is one the browser may refuse, because it no longer sees
          // it as the direct result of a click. Say so and offer the link instead.
          if (opened === null) {
            toast({
              title: "The attestation is ready",
              description: React.createElement(
                Link,
                { href: signedUrl, isExternal: true, textDecoration: "underline" },
                "Open the PDF"
              ),
              status: "success",
              position: "top-right",
              isClosable: true,
              duration: 15000,
            });
          }

          return;
        }

        setRunning({ id: attestationId, remaining: response.remaining });
        await sleep(POLL_INTERVAL_MS);
      }

      toast({
        title: "Still preparing",
        description:
          "This attestation has a lot of notes to prepare. Try again in a minute — the work " +
          "already done is kept.",
        status: "info",
        position: "top-right",
        isClosable: true,
        duration: 9000,
      });
    } catch (error) {
      toast({
        title: "Could not download the attestation",
        description: formatErrorResponse(error),
        status: "error",
        position: "top-right",
        isClosable: true,
        duration: 9000,
      });
    } finally {
      setRunning(null);
    }
  };

  return {
    downloadAll,
    /** True while THAT attestation is being prepared, so one spinner does not disable every row. */
    isPending: (attestationId: VisitInstanceDailyNoteAttestationId) =>
      running !== null && running.id === attestationId,
    /** Notes still being prepared for that attestation, once the server has said. */
    remaining: (attestationId: VisitInstanceDailyNoteAttestationId) =>
      running !== null && running.id === attestationId ? running.remaining : null,
  };
}
